<script setup lang="ts" generic="T extends SelectInputType = string">
import type { RadioButtonPassThroughOptions } from 'primevue/radiobutton';
import {
  computed,
  toRef,
} from 'vue';

import type { SelectInputType } from '@modules/forms/types';
import VFormInputWrapper
  from '@modules/forms/components/inputs/VFormInputWrapper/VFormInputWrapper.vue';

import type {
  VFormRadioInputEmits,
  VFormRadioInputProps,
} from './types';
import { useInputInnerValue } from '../composables';

const props = withDefaults(defineProps<VFormRadioInputProps<T>>(), {});
const emit = defineEmits<VFormRadioInputEmits<T>>();

const { innerValue } = useInputInnerValue(toRef(props, 'value'));

const radioButtonPt = computed<RadioButtonPassThroughOptions>(() => ({
  input: {
    class: props.readonly ? 'cursor-default' : undefined,
  },
  box(options) {
    const classes: string[] = [];

    if (props.readonly) {
      classes.push('radiobutton-readonly');

      if (options?.context?.checked) {
        classes.push('radiobutton-checked-box');
      }
    }
    return {
      class: classes.join(' '),
    };
  },
  icon(options) {
    return {
      class: props.readonly && options?.context?.checked ? 'radiobutton-checked-icon' : undefined,
    };
  },
}));
</script>

<template>
  <VFormInputWrapper v-data-qa-auto-id="'VFormRadioInput'"
    :errors="errors"
    :hide-errors="hideErrors"
    :readonly="readonly"
    :class="isFullWidth ? 'w-full' : 'input-max-width'"
  >
    <label
      v-if="label"
      class="mb-2 block text-muted-color"
    >
      {{ label }}
    </label>
    <div class="flex flex-wrap gap-4">
      <div
        v-for="option in options"
        :key="String(option.value)"
        class="flex items-center"
      >
        <PRadioButton
          v-model="innerValue"
          :input-id="`${name}-${option.value}`"
          :name="name"
          :value="option.value"
          :readonly="readonly"
          :disabled="disabled"
          :required="required"
          :pt="radioButtonPt"
          @change="emit('change', innerValue)"
          @input="emit('input', innerValue)"
          @blur="emit('blur', innerValue)"
          @focus="emit('focus', innerValue)"
        />
        <label
          :for="`${name}-${option.value}`"
          class="ml-2"
          :class="{ 'cursor-pointer': !readonly && !disabled }"
        >{{ option.label }}</label>
      </div>
    </div>
  </VFormInputWrapper>
</template>
